// Natural-language tasks: the phone runs the loop, this server's planner
// picks each step, the on-device broker approves or holds every action.

import type { TaskInfo } from "@fistos/shared/api";
import { Bot, ChevronDown, CircleCheck, CircleX, LoaderCircle, Sparkles, Square } from "lucide-solid";
import { createEffect, createMemo, createResource, createSignal, For, on, Show } from "solid-js";
import { toast } from "somoto";
import { ApprovalClassBadge, StatusBadge, TaskBadge, ToneBadge } from "@/components/badges";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch, SwitchControl, SwitchInput, SwitchLabel, SwitchThumb } from "@/components/ui/switch";
import { TextField, TextFieldInput, TextFieldLabel, TextFieldTextArea } from "@/components/ui/text-field";
import { api, errorText } from "@/lib/api";
import { now } from "@/lib/clock";
import { ago, clock, humanize, tokens } from "@/lib/format";
import { live, seedSteps, seedTasks } from "@/lib/live";
import type { PanelProps } from "./common";

const RUNNING = new Set(["pending", "running"]);

const EXAMPLES = [
  "Turn on Wi‑Fi and tell me which network it joins",
  "What's the battery level and is it charging?",
  "Open Settings and check for a system update",
];

function Composer(props: PanelProps & { onStarted: (t: TaskInfo) => void }) {
  const [goal, setGoal] = createSignal("");
  const [options, setOptions] = createSignal(false);
  const [maxSteps, setMaxSteps] = createSignal("");
  const [screenshot, setScreenshot] = createSignal(false);
  const [apps, setApps] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const blocked = () => !props.device.online || !!props.device.activeTaskId;

  const submit = async (e?: Event) => {
    e?.preventDefault();
    if (!goal().trim() || blocked()) return;
    setBusy(true);
    try {
      const t = await api.startTask(props.device.id, goal().trim(), {
        maxSteps: Number(maxSteps()) || undefined,
        alwaysScreenshot: screenshot(),
        allowedPackages: apps()
          .split(/[\s,]+/)
          .filter(Boolean),
      });
      setGoal("");
      props.onStarted(t);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card class="gap-4 py-5">
      <CardContent class="px-5">
        <form onSubmit={submit} class="grid gap-3">
          <TextField value={goal()} onChange={setGoal}>
            <TextFieldLabel class="flex items-center gap-1.5">
              <Sparkles class="size-4" /> What should the phone do?
            </TextFieldLabel>
            <TextFieldTextArea
              rows={3}
              placeholder={EXAMPLES[0]}
              class="resize-none"
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void submit();
              }}
            />
          </TextField>
          <Show when={!goal()}>
            <div class="flex flex-wrap gap-1.5">
              <For each={EXAMPLES}>
                {(ex) => (
                  <button
                    type="button"
                    class="text-muted-foreground hover:text-foreground hover:bg-accent rounded-full border px-2.5 py-0.5 text-xs transition-colors"
                    onClick={() => setGoal(ex)}
                  >
                    {ex}
                  </button>
                )}
              </For>
            </div>
          </Show>
          <button
            type="button"
            class="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1 text-xs"
            onClick={() => setOptions(!options())}
          >
            <ChevronDown class={`size-3.5 transition-transform ${options() ? "rotate-180" : ""}`} /> Options
          </button>
          <Show when={options()}>
            <div class="bg-muted/40 grid gap-3 rounded-lg border p-3">
              <div class="grid grid-cols-2 gap-3">
                <TextField value={maxSteps()} onChange={setMaxSteps}>
                  <TextFieldLabel class="text-xs">Max steps</TextFieldLabel>
                  <TextFieldInput type="number" min="1" max="200" placeholder="30" />
                </TextField>
                <TextField value={apps()} onChange={setApps}>
                  <TextFieldLabel class="text-xs">Only these apps</TextFieldLabel>
                  <TextFieldInput placeholder="com.android.settings" />
                </TextField>
              </div>
              <Switch class="flex items-center gap-2" checked={screenshot()} onChange={setScreenshot}>
                <SwitchInput />
                <SwitchControl>
                  <SwitchThumb />
                </SwitchControl>
                <SwitchLabel class="text-xs font-normal">Send a screenshot with every step</SwitchLabel>
              </Switch>
            </div>
          </Show>
          <div class="flex items-center justify-between gap-3">
            <span class="text-muted-foreground text-xs">
              <Show
                when={!props.device.online}
                fallback={<Show when={props.device.activeTaskId}>The phone is busy with a task.</Show>}
              >
                The phone is offline.
              </Show>
            </span>
            <Button type="submit" disabled={busy() || !goal().trim() || blocked()}>
              <Show when={busy()} fallback={<Bot />}>
                <LoaderCircle class="animate-spin" />
              </Show>
              Run
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function TaskDetail(props: { task: TaskInfo }) {
  const t = () => props.task;
  const [detail] = createResource(
    () => t().id,
    async (id) => {
      const d = await api.task(id);
      seedSteps(id, d.stepList);
      return d;
    },
  );
  const steps = () => live.steps[t().id] ?? [];
  const planner = () => live.planner[t().id];
  const running = () => RUNNING.has(t().state);
  const cancel = async () => {
    try {
      await api.cancelTask(t().id);
      toast("Cancelling…");
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  return (
    <Card class="gap-4 py-5">
      <CardHeader class="px-5">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <CardTitle class="text-base leading-snug">{t().goal}</CardTitle>
            <div class="text-muted-foreground mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              <TaskBadge state={t().state} />
              <span>{clock(t().createdAt)}</span>
              <Show when={t().planner}>
                <span>· {t().planner}</span>
              </Show>
              <Show when={t().usage.calls}>
                <span>
                  · {tokens(t().usage.inputTokens)} in / {tokens(t().usage.outputTokens)} out
                </span>
              </Show>
            </div>
          </div>
          <Show when={running()}>
            <Button size="sm" variant="outline" onClick={cancel}>
              <Square /> Stop
            </Button>
          </Show>
        </div>
      </CardHeader>
      <CardContent class="grid gap-4 px-5">
        <Show when={!running() && t().summary}>
          <Alert variant={t().state === "succeeded" ? "default" : "destructive"}>
            <Show when={t().state === "succeeded"} fallback={<CircleX />}>
              <CircleCheck class="!text-success" />
            </Show>
            <AlertTitle>{t().state === "succeeded" ? "Done" : humanize(t().state)}</AlertTitle>
            <AlertDescription>{t().summary}</AlertDescription>
          </Alert>
        </Show>

        <ol class="relative grid gap-0">
          <For each={steps()}>
            {(s, i) => (
              <li class="relative flex gap-3 pb-4">
                <Show when={i() < steps().length - 1 || running()}>
                  <span class="bg-border absolute top-6 bottom-0 left-[11px] w-px" />
                </Show>
                <span class="bg-muted text-muted-foreground z-10 flex size-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-medium tabular-nums">
                  {s.step + 1}
                </span>
                <div class="min-w-0 flex-1 space-y-1 pt-0.5">
                  <div class="flex flex-wrap items-center gap-1.5">
                    <span class="font-mono text-[13px] break-all">{s.actionText}</span>
                    <StatusBadge status={s.status} />
                    <ApprovalClassBadge value={s.approval} />
                  </div>
                  <Show when={s.rationale}>
                    <p class="text-muted-foreground text-sm">{s.rationale}</p>
                  </Show>
                  <Show when={s.message && s.status !== "OK"}>
                    <p class="text-destructive text-xs">{s.message}</p>
                  </Show>
                  <Show when={s.evidence.length}>
                    <div class="flex flex-wrap gap-1">
                      <For each={s.evidence}>
                        {(ev) => (
                          <span class="bg-muted text-muted-foreground rounded px-1.5 py-px font-mono text-[11px]">
                            {ev}
                          </span>
                        )}
                      </For>
                    </div>
                  </Show>
                </div>
              </li>
            )}
          </For>
          <Show when={running()}>
            <li class="flex items-center gap-3">
              <span class="z-10 flex size-6 items-center justify-center">
                <LoaderCircle class="text-muted-foreground size-4 animate-spin" />
              </span>
              <span class="text-muted-foreground text-sm">
                <Show
                  when={planner()?.state === "thinking"}
                  fallback={t().state === "pending" ? "Starting on the phone…" : "Working on the phone…"}
                >
                  Planning step {(planner()?.step ?? 0) + 1}…
                </Show>
              </span>
            </li>
          </Show>
          <Show when={!running() && !steps().length && !detail.loading}>
            <li class="text-muted-foreground text-sm">No steps were taken.</li>
          </Show>
        </ol>
        <Show when={planner()?.state === "error"}>
          <p class="text-destructive text-xs">Planner: {planner()!.detail}</p>
        </Show>
      </CardContent>
    </Card>
  );
}

export function AgentPanel(props: PanelProps) {
  const [list] = createResource(
    () => props.device.id,
    async (id) => {
      const tasks = await api.tasks(id);
      seedTasks(tasks);
      return tasks;
    },
  );
  const tasks = createMemo(() =>
    Object.values(live.tasks)
      .filter((t) => t.deviceId === props.device.id)
      .sort((a, b) => b.createdAt - a.createdAt),
  );
  const [selected, setSelected] = createSignal<string | null>(null);
  const current = () => tasks().find((t) => t.id === selected()) ?? tasks()[0];
  // Follow a newly started task.
  createEffect(
    on(
      () => tasks()[0]?.id,
      (id) => id && RUNNING.has(tasks()[0].state) && setSelected(id),
    ),
  );

  return (
    <div class="grid gap-5 *:min-w-0 lg:grid-cols-5">
      <div class="grid content-start gap-5 *:min-w-0 lg:col-span-2">
        <Composer device={props.device} caps={props.caps} onStarted={(t) => setSelected(t.id)} />
        <Show when={tasks().length}>
          <div class="grid gap-1 *:min-w-0">
            <div class="text-muted-foreground px-1 pb-1 text-xs font-medium">Recent tasks</div>
            <For each={tasks().slice(0, 20)}>
              {(t) => (
                <button
                  class="hover:bg-accent flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors"
                  classList={{ "bg-accent": current()?.id === t.id }}
                  onClick={() => setSelected(t.id)}
                >
                  <span class="min-w-0 flex-1 truncate">{t.goal}</span>
                  <span class="text-muted-foreground shrink-0 text-xs">{ago(t.createdAt, now())}</span>
                  <TaskBadge state={t.state} />
                </button>
              )}
            </For>
          </div>
        </Show>
      </div>
      <div class="lg:col-span-3">
        <Show
          when={current()}
          fallback={
            <Card class="text-muted-foreground items-center gap-2 border-dashed p-10 text-center text-sm">
              <Bot class="size-6" />
              <Show when={!list.loading} fallback="Loading…">
                Describe a goal and the phone works through it step by step. Sensitive steps wait for your approval.
              </Show>
            </Card>
          }
        >
          {(t) => (
            <Show when={t().id} keyed>
              <TaskDetail task={t()} />
            </Show>
          )}
        </Show>
        <Show when={props.caps && !props.caps.accessibility}>
          <div class="mt-3">
            <ToneBadge tone="warn">
              Accessibility is off on the phone: the agent can only use screenshots and taps.
            </ToneBadge>
          </div>
        </Show>
      </div>
    </div>
  );
}
